import React, { Fragment, useMemo, useState } from 'react';
import { Cell, ColumnDef, flexRender, Header, HeaderGroup, Row } from '@tanstack/react-table';
import {
  Box,
  Checkbox,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { cn } from '../Utils/cnUtil';
import { useTanstackTable } from './useTanstackTable';
import TanstackTableHeaderCell from './TanstackTableHeader';
import TanstackTableToolbar from './TanstackTableToolbar';
import TanstackTablePagination from './TanstackTablePagination';
import { TanstackTableDensity, TanstackTableProps } from './types';
import { DEFAULT_PAGE_SIZE_OPTIONS } from './constants';
import { TanstackTableLocaleContext, useTanstackTableLocale } from './locale';
import { getColumnSizingStyle, getDensityPadding, getPinningStyles, toMuiAlign } from './utils';
import { TANSTACK_TABLE_PART } from './parts';

/**
 * A TanStack-Table-powered data grid rendered with MUI primitives.
 *
 * - `mode="client"` (default): TanStack performs sorting, filtering, faceting,
 *   pagination, grouping and expansion in the browser.
 * - `mode="server"`: every feature is manual. Drive it by passing controlled
 *   `state` plus the matching `onXChange` callbacks (and `rowCount`); the table
 *   renders whatever `data` slice you hand it while keeping the same UX.
 *
 * It renders and nothing more. It measures nothing, owns no layout state and has
 * no idea a page size might be fitted to the viewport — it only labels its parts
 * via `TANSTACK_TABLE_PART` and exposes its root through `rootRef`, so something
 * outside can walk in and measure. `useAutoPageSize` is that something.
 */
function TanstackTable<TData>(props: TanstackTableProps<TData>): JSX.Element {
  const {
    isLoading = false,
    error,
    emptyMessage = 'No rows',
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    onPageSizePicked,
    rootRef,
    renderSizingRow = false,
    maxScrollAreaHeight,
    showToolbar = true,
    toolbarActions,
    enableDensityToggle = true,
    enablePagination = true,
    enableGlobalFilter = true,
    enableColumnVisibility = true,
    enableRowSelection = false,
    stickyHeader = true,
    classNames,
    className,
    onRowClick,
    renderSubComponent,
    onDensityChange,
  } = props;

  const table = useTanstackTable(props);

  // Density follows the standard controlled/uncontrolled pattern:
  //  - no props                       -> uncontrolled, defaults to 'standard'
  //  - `density` only                 -> uncontrolled, `density` is the initial value
  //  - `density` + `onDensityChange`  -> controlled by the parent
  const isDensityControlled = onDensityChange !== undefined;
  const [internalDensity, setInternalDensity] = useState<TanstackTableDensity>(
    props.density ?? 'standard'
  );
  const density = isDensityControlled ? props.density ?? 'standard' : internalDensity;
  const handleDensityChange = (next: TanstackTableDensity): void => {
    if (!isDensityControlled) setInternalDensity(next);
    onDensityChange?.(next);
  };
  const cellPadding = getDensityPadding(density);

  const showError = error !== undefined && error !== null && error !== false;

  // Inherit from an app-level TanstackTableLocaleProvider (English if absent),
  // and let a per-table `localeText` prop override individual strings.
  const inheritedLocale = useTanstackTableLocale();
  const locale = useMemo(
    () => ({ ...inheritedLocale, ...props.localeText }),
    [inheritedLocale, props.localeText]
  );
  const hasGrowColumn = useMemo(
    () => props.columns.some((column: ColumnDef<TData, unknown>) => column.meta?.grow),
    [props.columns]
  );

  const showSelectionColumn = enableRowSelection !== false && enableRowSelection !== undefined;
  const showExpandColumn = Boolean(renderSubComponent || props.getSubRows);
  const leadingColumns = (showSelectionColumn ? 1 : 0) + (showExpandColumn ? 1 : 0);
  const totalColumns = table.getVisibleLeafColumns().length + leadingColumns;

  const rows = table.getRowModel().rows;

  const renderBodyCell = (cell: Cell<TData, unknown>, row: Row<TData>): JSX.Element => {
    const align = cell.column.columnDef.meta?.align ?? 'left';
    let content: React.ReactNode;

    if (cell.getIsGrouped()) {
      content = (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ cursor: 'pointer', fontWeight: 600 }}
          onClick={row.getToggleExpandedHandler()}
        >
          <IconButton size="small">
            {row.getIsExpanded() ? (
              <KeyboardArrowDownIcon fontSize="inherit" />
            ) : (
              <KeyboardArrowRightIcon fontSize="inherit" />
            )}
          </IconButton>
          <span>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
          <span>({row.subRows.length})</span>
        </Stack>
      );
    } else if (cell.getIsAggregated()) {
      content = flexRender(
        cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
        cell.getContext()
      );
    } else if (cell.getIsPlaceholder()) {
      content = null;
    } else {
      content = flexRender(cell.column.columnDef.cell, cell.getContext());
    }

    return (
      <TableCell
        key={cell.id}
        align={toMuiAlign(align)}
        className={cn(classNames?.cell)}
        style={{
          ...getColumnSizingStyle(cell.column, hasGrowColumn),
          ...getPinningStyles(cell.column),
        }}
        sx={{ padding: cellPadding, backgroundColor: 'inherit' }}
      >
        {content}
      </TableCell>
    );
  };

  return (
    <TanstackTableLocaleContext.Provider value={locale}>
      <Paper
        ref={rootRef}
        {...{ [TANSTACK_TABLE_PART.root]: '' }}
        variant="outlined"
        className={cn('flex flex-col', className)}
      >
        {showToolbar && (
          <div {...{ [TANSTACK_TABLE_PART.toolbar]: '' }}>
            <TanstackTableToolbar
              table={table}
              showGlobalFilter={enableGlobalFilter}
              showColumnVisibility={enableColumnVisibility}
              enableDensityToggle={enableDensityToggle}
              density={density}
              onDensityChange={handleDensityChange}
              actions={toolbarActions}
            />
          </div>
        )}

        <TableContainer
          className={cn('relative overflow-auto', classNames?.container)}
          style={maxScrollAreaHeight !== undefined ? { maxHeight: maxScrollAreaHeight } : undefined}
        >
          {isLoading && (
            <LinearProgress sx={{ position: 'sticky', top: 0, left: 0, right: 0, zIndex: 3 }} />
          )}
          <Table
            stickyHeader={stickyHeader}
            size={density === 'compact' ? 'small' : 'medium'}
            className={cn(classNames?.table)}
            sx={{ width: '100%' }}
          >
            <TableHead {...{ [TANSTACK_TABLE_PART.head]: '' }}>
              {table.getHeaderGroups().map((headerGroup: HeaderGroup<TData>) => (
                <TableRow key={headerGroup.id} sx={{ backgroundColor: 'background.paper' }}>
                  {showSelectionColumn && (
                    <TableCell padding="checkbox" sx={{ backgroundColor: 'background.paper' }}>
                      <Checkbox
                        size="small"
                        checked={table.getIsAllPageRowsSelected()}
                        indeterminate={
                          table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected()
                        }
                        onChange={table.getToggleAllPageRowsSelectedHandler()}
                      />
                    </TableCell>
                  )}
                  {showExpandColumn && (
                    <TableCell sx={{ width: 48, backgroundColor: 'background.paper' }} />
                  )}
                  {headerGroup.headers.map((header: Header<TData, unknown>) => (
                    <TanstackTableHeaderCell
                      key={header.id}
                      header={header}
                      table={table}
                      facets={props.facets}
                      hasGrowColumn={hasGrowColumn}
                    />
                  ))}
                </TableRow>
              ))}
            </TableHead>

            <TableBody {...{ [TANSTACK_TABLE_PART.body]: '' }}>
              {/*
                With no data there is no row to measure, and guessing the height
                costs a whole extra round trip in server mode: fetch at the
                guessed size, then correct once real rows render. This invisible
                row is laid out exactly like a real one, so the first
                measurement — and therefore the first request — is already right.
              */}
              {renderSizingRow && rows.length === 0 && (
                <TableRow
                  {...{ [TANSTACK_TABLE_PART.row]: '' }}
                  aria-hidden
                  sx={{ visibility: 'hidden' }}
                >
                  <TableCell colSpan={totalColumns} sx={{ padding: cellPadding, border: 0 }}>
                    &nbsp;
                  </TableCell>
                </TableRow>
              )}

              {showError && !isLoading && (
                <TableRow>
                  <TableCell colSpan={totalColumns} sx={{ border: 0, py: 6 }}>
                    <Stack alignItems="center" spacing={1} sx={{ color: 'error.main' }}>
                      <ErrorOutlineIcon sx={{ fontSize: 44 }} />
                      {typeof error === 'string' ? (
                        <Typography variant="body2" color="error">
                          {error}
                        </Typography>
                      ) : (
                        error
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {!showError && rows.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={totalColumns} sx={{ border: 0, py: 6 }}>
                    <Stack alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
                      <InboxOutlinedIcon sx={{ fontSize: 44, opacity: 0.4 }} />
                      {typeof emptyMessage === 'string' ? (
                        <Typography variant="body2" color="text.secondary">
                          {emptyMessage}
                        </Typography>
                      ) : (
                        emptyMessage
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {!showError &&
                rows.map((row: Row<TData>) => {
                  const canExpandRow =
                    Boolean(renderSubComponent) || (showExpandColumn && row.getCanExpand());
                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        {...{ [TANSTACK_TABLE_PART.row]: '' }}
                        hover
                        selected={row.getIsSelected()}
                        className={cn(classNames?.row)}
                        onClick={onRowClick ? (): void => onRowClick(row) : undefined}
                        sx={{
                          cursor: onRowClick ? 'pointer' : 'default',
                          backgroundColor: 'background.paper',
                          '&.Mui-selected': { backgroundColor: 'action.selected' },
                        }}
                      >
                        {showSelectionColumn && (
                          <TableCell
                            padding="checkbox"
                            sx={{ backgroundColor: 'inherit' }}
                            onClick={(e: React.MouseEvent<HTMLElement>): void =>
                              e.stopPropagation()
                            }
                          >
                            <Checkbox
                              size="small"
                              checked={row.getIsSelected()}
                              disabled={!row.getCanSelect()}
                              indeterminate={row.getIsSomeSelected()}
                              onChange={row.getToggleSelectedHandler()}
                            />
                          </TableCell>
                        )}
                        {showExpandColumn && (
                          <TableCell
                            sx={{ backgroundColor: 'inherit' }}
                            onClick={(e: React.MouseEvent<HTMLElement>): void =>
                              e.stopPropagation()
                            }
                          >
                            {canExpandRow && (
                              <IconButton size="small" onClick={(): void => row.toggleExpanded()}>
                                {row.getIsExpanded() ? (
                                  <KeyboardArrowDownIcon fontSize="inherit" />
                                ) : (
                                  <KeyboardArrowRightIcon fontSize="inherit" />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                        {row
                          .getVisibleCells()
                          .map((cell: Cell<TData, unknown>) => renderBodyCell(cell, row))}
                      </TableRow>

                      {renderSubComponent && row.getIsExpanded() && (
                        <TableRow>
                          <TableCell
                            colSpan={totalColumns}
                            sx={{ backgroundColor: 'action.hover' }}
                          >
                            {renderSubComponent(row)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {enablePagination && (
          <Box
            {...{ [TANSTACK_TABLE_PART.pagination]: '' }}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          >
            <TanstackTablePagination
              table={table}
              pageSizeOptions={pageSizeOptions}
              rowCount={props.mode === 'server' ? props.rowCount : undefined}
              onPageSizePicked={onPageSizePicked}
            />
          </Box>
        )}
      </Paper>
    </TanstackTableLocaleContext.Provider>
  );
}

export default TanstackTable as <TData>(props: TanstackTableProps<TData>) => JSX.Element;
